import { useSyncExternalStore } from 'react';

/**
 * "Add to home screen", offered by the app itself.
 *
 * Chrome on Android fires `beforeinstallprompt` once, early — often before
 * React has mounted — and the event has to be held on to and replayed from a
 * tap. So it is caught here, at module load, and exposed as a tiny store.
 *
 * Riders who are not at ease with phones will not find "Install app" in the
 * browser's three-dot menu. A button on the run sheet that says what it does
 * is the difference between the app being installed and not.
 */

type InstallEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Hold it; the browser's own mini-infobar is easy to dismiss by accident.
    event.preventDefault();
    deferred = event as InstallEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Whether the app can offer its own install button right now. */
export function useCanInstall(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => deferred !== null,
    () => false,
  );
}

/** Shows the browser's install dialog. Must be called from a tap. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const event = deferred;
  deferred = null;
  notify();
  await event.prompt();
  const choice = await event.userChoice;
  return choice.outcome === 'accepted';
}
